import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => ({
    create_tags: null,

    documents: [],
    documents_filters: DEFAULT_FILTERS,
    documents_count: 0,
    documents_loaded: false,
    documents_loading: false,
    documents_loading_errors: null,

    document_saving: false,
    document_saving_errors: null,

    shared_with: null,
    cancelTokenSource: null,
    _storageOptions: options || {},
});

const mutations = {
    updateTags(state, params) {
        state.create_tags = params.create_tags;
        state.documents_filters = {
            offset: 0,
            limit: state.documents_filters.limit,
            tags: params.tags,
        };
        state.shared_with = params.shared_with;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.documents_filters, fieldsToSave);
        }
    },
    updateDocumentsFilters(state, filters) {
        state.documents_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.documents_filters, fieldsToSave);
        }
    },
    updateDocuments(state, data) {
        state.documents = data?.results;
        state.documents_count = data?.count;
        state.documents_loading = false;
        state.documents_loading_errors = null;
    },
    updateDocumentsLoading(state, documents_loading) {
        state.documents_loading = documents_loading;
    },
    updateDocumentsLoadingErrors(state, documents_loading_errors) {
        state.documents_loading_errors = documents_loading_errors;
        state.documents_loading = false;
    },
    updateDocumentsLoaded(state, documents_loaded) {
        state.documents_loaded = documents_loaded;
    },
    updateDocumentSaving(state, document_saving) {
        state.document_saving = document_saving;
    },
    updateDocumentSavingErrors(state, document_saving_errors) {
        state.document_saving_errors = document_saving_errors;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },

};

const actions = {

    async deleteDocument({ commit, dispatch, state }, document) {
        try {
            const response = await axios.post(`/api/documents/${document.id}/archive/`);
            await dispatch('fetchDocuments');
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            await dispatch('fetchDocuments');
            throw error;
        }
    },

    async uploadDocument({ commit, dispatch, state }, params) {
        let headers = { 'Content-Type': 'multipart/form-data' };
        let data = new FormData();

        data.append("file", params.file);
        state.create_tags.forEach((tag) => {
            data.append("tags", tag);
        })
        if (state.shared_with) {
            data.append('shared_with', state.shared_with);
            data.append('shared', true);
        }

        commit('updateDocumentSavingErrors', null);
        commit('updateDocumentSaving', true);

        try {
            const response = await axios.post('/api/documents/', data, { headers });
            await dispatch('fetchDocuments');
            commit('updateDocumentSaving', false);
            return { document: response.data };
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateDocumentSavingErrors', error.details);
            await dispatch('fetchDocuments');
            commit('updateDocumentSaving', false);
            throw error;
        }
    },

    async fetchDocuments({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("core.view_document") == -1) {
            commit('updateDocuments', { results: [], count: 0 });
            commit('updateDocumentsLoaded', true);
            commit('updateDocumentsLoading', false);
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateDocumentsLoading', true);
        commit('updateDocumentsLoadingErrors', null);

        try {
            const response = await axios.get("/api/documents/", {
                params: state.documents_filters,
                cancelToken: source.token,
            });
            commit('updateDocuments', response.data);
            commit('updateDocumentsLoaded', true);
            commit('updateDocumentsLoading', false);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateDocumentsLoadingErrors', error.details);
            commit('updateDocumentsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateDocuments', null);
        commit('updateDocumentsLoaded', false);
        
        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        const persistedFilters = state._storageOptions?.storageKey 
            ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) 
            : {};
        
        // Merge with params (tags, shared_with, etc.)
        const filters = {
            ...DEFAULT_FILTERS,
            ...persistedFilters,
            ...(params.tags ? { tags: params.tags } : {}),
        };
        
        await commit('updateDocumentsFilters', filters);
        
        if (params.create_tags !== undefined || params.shared_with !== undefined) {
            commit('updateTags', {
                create_tags: params.create_tags,
                tags: params.tags,
                shared_with: params.shared_with,
            });
        }
    }
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});